import { KeyRound } from 'lucide-react';
import { useConfigTextCredentials, type ReferencedCredential } from '@/hooks/useCredentialReferences';
import { splitReferences, type CredentialReference } from '@/utils/credentialReferences';
import { cn } from '@/lib/utils';

/**
 * Stored config text, with each credential reference shown as a chip: which
 * credential, at which version, and whether a restore would write something else
 * (website-backend#81). The value is never here; only the gateways hold it.
 */

function chip(reference: CredentialReference, credential: ReferencedCredential | undefined) {
  if (!reference.id) {
    return {
      label: 'redacted',
      tone: 'muted' as const,
      title: 'Redacted: no gateway kept this value, so a restore refuses this line.',
    };
  }
  if (!credential) {
    return { label: `credential v${String(reference.version)}`, tone: 'muted' as const, title: 'A credential' };
  }
  const name =
    credential.name ?? (credential.placeholder ? 'from device config' : 'a credential you can’t view');
  const label = `${name} v${String(reference.version)}`;
  if (credential.deleted) {
    return {
      label: `${label} · deleted`,
      tone: 'danger' as const,
      title: 'Deleted: a restore can’t write this line.',
    };
  }
  if (credential.placeholder) {
    return {
      label: credential.raw ? `${name} · hash as found` : name,
      tone: 'muted' as const,
      title: credential.raw
        ? 'A hash or encrypted value as the device wrote it: it rebuilds this config, and signs nothing in.'
        : 'A value found in this device’s config. Witina keeps it on the gateways only to rebuild the config.',
    };
  }
  if (reference.version !== null && reference.version < credential.version) {
    return {
      label: `${label} · rotated to v${String(credential.version)}`,
      tone: 'warn' as const,
      title: `This config held v${String(reference.version)}. A restore writes v${String(credential.version)}, the current version.`,
    };
  }
  return { label, tone: 'ok' as const, title: `${name}, version ${String(reference.version)}` };
}

const TONE = {
  ok: 'bg-primary/10 text-primary',
  warn: 'bg-sev-3-soft text-sev-3',
  danger: 'bg-destructive/15 text-destructive',
  muted: 'bg-muted text-muted-foreground',
};

/** One line of config text, references as chips. */
export function ConfigLine({
  line,
  credentials,
}: {
  line: string;
  credentials: Record<string, ReferencedCredential>;
}) {
  return (
    <>
      {splitReferences(line).map((part, i) => {
        if (typeof part === 'string') return <span key={i}>{part}</span>;
        const c = chip(part, part.id ? credentials[part.id] : undefined);
        return (
          <span
            key={i}
            title={c.title}
            data-reference={part.raw}
            className={cn(
              'inline-flex items-center gap-0.5 rounded px-1 align-baseline font-sans text-[10px] leading-none',
              TONE[c.tone],
            )}
          >
            <KeyRound className="h-2.5 w-2.5 shrink-0" aria-hidden />
            {c.label}
          </span>
        );
      })}
    </>
  );
}

/** A whole config text, in a `<pre>`, references as chips. */
export default function ConfigText({ text, className }: { text: string; className?: string }) {
  const credentials = useConfigTextCredentials([text]);
  return (
    <pre className={className}>
      {text.split('\n').map((line, i, lines) => (
        <span key={i}>
          <ConfigLine line={line} credentials={credentials} />
          {i < lines.length - 1 ? '\n' : null}
        </span>
      ))}
    </pre>
  );
}
